/**
 * Records UI Mapper
 *
 * Transforms API types (RecordListItem/RecordWithDetails) to UI types (RecordItem).
 *
 * SSOT Pipeline: COMPLETE
 * =======================
 * - API types from: @aica/api-types (auto-generated from OpenAPI)
 * - UI types: defined here (UI-specific, not generated)
 * - Mapper: pure function, no side effects, testable
 *
 * Usage:
 *   import { toRecordItem, toRecordItems } from '@/lib/api/mappers/records.mapper';
 *   const uiRecords = toRecordItems(apiRecords, t);
 */

import type {
  RecordListItem,
  CrmStageSemantic,
  CrmStageSource,
  CallSeriesOutcomeSemantic,
} from '@aica/api-types';
import type {
  ApiRecordWithDetails,
  RecordWithDetails as UiRecordWithDetails,
  MediaMeta,
  MediaSource,
} from '../types/records.types';

// Status type from generated OpenAPI (for type safety)
type ApiRecordStatus = RecordListItem['status'];

/**
 * UI Record type - optimized for display in DataGrid/Lists
 *
 * Differences from API type:
 * - `title`: computed from externalId or generated
 * - `project`: resolved name or fallback to projectId
 * - `channel`: preserves API channel type ('call' | 'chat' | 'email')
 * - `duration`: formatted string "M:SS" instead of seconds
 * - `status`: resolved from state/status with priority logic
 * - `createdAt`: formatted locale string
 * - `participantName`: with fallback
 */
export interface RecordItem {
  id: string;
  title: string;
  project: string;
  projectId: string;
  // TODO(ui): components that render channel icons/labels should handle 'email'
  // (currently they only check for 'call' vs 'chat' and will fall through to the
  // chat branch for email records, which is acceptable until a dedicated email
  // icon/label is added)
  channel: 'call' | 'chat' | 'email';
  duration?: string;
  messagesCount?: number;
  status: ApiRecordStatus;
  /** Demo/sample data (#1503): shown in the funnel, never started — the start excludes it. */
  isDemo?: boolean;
  createdAt: string;
  /** Event time from CRM (START_TIME) - the actual time of the call/chat */
  eventTime?: string;
  participantName?: string;
  participantPhone?: string;
  participantEmail?: string;
  progressPercent?: number;
  relevanceScore?: number; // For semantic search results (0-1)
  agentId?: string;
  agentName?: string;
  agentPhone?: string;
  // CRM Entity fields
  crmActivityName?: string;
  crmEntityType?: 'deal' | 'lead' | 'contact' | 'company';
  crmEntityId?: string;
  crmEntityName?: string;
  /**
   * Which CRM the entity lives in, and its portal domain — carried per RECORD,
   * not per org. An org with both a Bitrix24 and a Uspacy install has two
   * portals, and the org-level `bitrix24Domain` answered for both, so a Uspacy
   * card's link opened somebody else's Bitrix24 card. Undefined = no portal
   * known; the link renders as plain text.
   */
  crmVendor?: string;
  crmDomain?: string;
  // Stage snapshot fields (Bitrix24 deal/lead stage at call time, Phase B).
  // IDs only — names are resolved client-side from the CRM stages reference.
  crmStageId?: string;
  crmPipelineId?: string;
  crmStageSemantic?: CrmStageSemantic;
  crmStageSource?: CrmStageSource;
  // External ID from CRM (e.g., Bitrix24 activity ID)
  externalId?: string;
  // Short call flag (Level 2 filter)
  isShortCall?: boolean;
  // Project's min call duration setting (for short call banner)
  minCallDurationSeconds?: number;
  // Call category (automatic categorization)
  callCategory?: string;
  // Call direction (inbound/outbound)
  callDirection?: 'inbound' | 'outbound';
  // Overall quality score from analysis (0-100)
  qualityScore?: number;
  // Latest-analysis quality fields (null = scoreless, never 0)
  saleProbability?: number | null;
  sentiment?: 'positive' | 'neutral' | 'negative' | null;
  greetingScore?: number | null;
  needsScore?: number | null;
  objectionScore?: number | null;
  closingScore?: number | null;
  politenessScore?: number | null;
  clarityScore?: number | null;
  // Agent's rating of AI analysis accuracy (1–5 stars)
  agentRating?: number | null;
  agentRatingText?: string | null;
  agentRatingAt?: string | null;
  agentRatingUserId?: string | null;
  agentRatingUserName?: string | null;
  // Call series fields
  callSeriesId?: string;
  callSeriesNumber?: number;
  callSeriesEntityType?: 'deal' | 'lead';
  callSeriesEntityId?: string;
  callSeriesTotal?: number;
  // Deal outcome badge (Phase C, C-4). Undefined/null hides the badge.
  dealOutcome?: CallSeriesOutcomeSemantic | null;
}

/**
 * Translation function type (from react-i18next)
 */
export type TranslationFn = (key: string) => string;

/**
 * Format duration from seconds to "M:SS" string
 *
 * @example
 * formatDuration(125) // "2:05"
 * formatDuration(60)  // "1:00"
 * formatDuration(undefined) // undefined
 */
export function formatDuration(seconds: number | null | undefined): string | undefined {
  if (seconds == null) return undefined;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
}

/**
 * Map record type to UI channel
 *
 * Preserves the original channel type so email records are not silently
 * conflated with chat records. Components rendering channel icons/labels
 * should handle all three values; until they do, 'email' will fall through
 * to chat-style display (acceptable degraded rendering).
 */
export function mapTypeToChannel(
  type: 'call' | 'chat' | 'email'
): 'call' | 'chat' | 'email' {
  return type;
}

/**
 * Get display status from unified status field
 *
 * NOTE: The `state` field has been removed. All status information
 * is now in the unified `status` field which supports all pipeline stages.
 */
export function resolveDisplayStatus(status: ApiRecordStatus): ApiRecordStatus {
  return status;
}

/**
 * Generate record title from externalId or ID
 *
 * @param externalId - External ID from CRM (e.g., "DEAL-123")
 * @param id - Internal record UUID
 * @param typeLabel - Translated type label (e.g., "Call", "Chat")
 */
export function generateTitle(
  externalId: string | null | undefined,
  id: string,
  typeLabel: string
): string {
  if (externalId) return externalId;
  return `${typeLabel} #${id.substring(0, 8)}`;
}

/**
 * Transform a single API record to UI format
 *
 * @param record - API record (RecordListItem from @aica/api-types)
 * @param t - Translation function for labels
 * @returns UI record (RecordItem)
 *
 * @example
 * const uiRecord = toRecordItem(apiRecord, t);
 */
export function toRecordItem(record: RecordListItem, t: TranslationFn): RecordItem {
  const typeLabel = record.type === 'call' ? t('records.call') : t('records.chat');

  return {
    id: record.id,
    title: generateTitle(record.externalId, record.id, typeLabel),
    project: record.projectName || record.projectId,
    projectId: record.projectId,
    channel: mapTypeToChannel(record.type),
    duration: formatDuration(record.durationSeconds),
    // messagesCount: not available in API (future enhancement)
    status: resolveDisplayStatus(record.status),
    isDemo: record.isDemo,
    createdAt: record.createdAt ?? '',
    // Event time from CRM (START_TIME) - the actual time of the call/chat
    eventTime: record.eventTime ?? undefined,
    participantName:
      record.participantName || record.participantPhone || t('records.unknown'),
    // progressPercent: not in current API (would come from stage_statuses)
    agentId: record.agentId ?? undefined,
    agentName: record.agentName ?? undefined,
    agentPhone: record.agentPhone ?? undefined,
    participantPhone: record.participantPhone ?? undefined,
    // CRM Entity fields
    crmActivityName: record.crmActivityName ?? undefined,
    crmEntityType: record.crmEntityType ?? undefined,
    crmEntityId: record.crmEntityId ?? undefined,
    crmEntityName: record.crmEntityName ?? undefined,
    crmVendor: record.crmVendor ?? undefined,
    crmDomain: record.crmDomain ?? undefined,
    // Stage snapshot fields (Phase B)
    crmStageId: record.crmStageId ?? undefined,
    crmPipelineId: record.crmPipelineId ?? undefined,
    crmStageSemantic: record.crmStageSemantic ?? undefined,
    crmStageSource: record.crmStageSource ?? undefined,
    // External ID from CRM
    externalId: record.externalId ?? undefined,
    // Short call flag
    isShortCall: record.isShortCall ?? undefined,
    // Call category
    callCategory: record.callCategory ?? undefined,
    // Call direction
    callDirection: record.callDirection ?? undefined,
    // Overall quality score from latest analysis (0-100)
    qualityScore: record.qualityScore ?? undefined,
    // Latest-analysis quality fields (null preserved — scoreless is not 0)
    saleProbability: record.saleProbability ?? null,
    sentiment: record.sentiment ?? null,
    greetingScore: record.greetingScore ?? null,
    needsScore: record.needsScore ?? null,
    objectionScore: record.objectionScore ?? null,
    closingScore: record.closingScore ?? null,
    politenessScore: record.politenessScore ?? null,
    clarityScore: record.clarityScore ?? null,
    // Agent's rating of AI analysis accuracy (1–5 stars)
    agentRating: record.agentRating ?? null,
    agentRatingText: record.agentRatingText ?? null,
    agentRatingAt: record.agentRatingAt ?? null,
    agentRatingUserId: record.agentRatingUserId ?? null,
    agentRatingUserName: record.agentRatingUserName ?? null,
    // Call series fields
    callSeriesId: record.callSeriesId ?? undefined,
    callSeriesNumber: record.callSeriesNumber ?? undefined,
    callSeriesEntityType: record.callSeriesEntityType ?? undefined,
    callSeriesEntityId: record.callSeriesEntityId ?? undefined,
    callSeriesTotal: record.callSeriesTotal ?? undefined,
    // Deal outcome badge (Phase C, C-4)
    dealOutcome: record.dealOutcome ?? null,
  };
}

/**
 * Transform an array of API records to UI format
 *
 * @param records - Array of API records (RecordListItem[] from @aica/api-types)
 * @param t - Translation function for labels
 * @returns Array of UI records
 *
 * @example
 * const { records } = await api.getRecords();
 * const uiRecords = toRecordItems(records, t);
 */
export function toRecordItems(records: RecordListItem[], t: TranslationFn): RecordItem[] {
  return records.map((record) => toRecordItem(record, t));
}

/**
 * Add relevance score to a UI record (for semantic search results)
 *
 * @param record - UI record
 * @param score - Relevance score (0-1)
 * @returns UI record with relevance score
 */
export function withRelevanceScore(record: RecordItem, score: number): RecordItem {
  return { ...record, relevanceScore: score };
}

// ============================================================================
// API → UI Record Transformation (for detailed views)
// ============================================================================

/**
 * Safely parse JSON string to object
 * Returns undefined if parsing fails or input is null/undefined
 */
function safeJsonParse<T>(json: string | null | undefined): T | undefined {
  if (!json) return undefined;
  try {
    return JSON.parse(json) as T;
  } catch {
    return undefined;
  }
}

/**
 * Transform API RecordWithDetails to UI RecordWithDetails
 *
 * Key transformations:
 * - `mediaMeta`: JSON string → MediaMeta object (parsed)
 * - `expectedMediaSources`: JSON string → MediaSource[] array (parsed)
 * - `createdAt`: ensure string (default to now if null)
 * - `archived`: ensure boolean (default to false if null)
 *
 * Status and State enums are now aligned between API and UI (SSOT).
 *
 * @param apiRecord - API response (from @aica/api-types)
 * @returns UI-friendly record type with parsed JSON fields
 */
export function toUiRecordWithDetails(
  apiRecord: ApiRecordWithDetails
): UiRecordWithDetails {
  return {
    ...apiRecord,
    // Ensure required fields have defaults
    createdAt: apiRecord.createdAt ?? new Date().toISOString(),
    archived: apiRecord.archived ?? false,
    // Convert null to undefined for optional fields (UI types don't use null)
    installationId: apiRecord.installationId ?? undefined,
    externalId: apiRecord.externalId ?? undefined,
    fileUri: apiRecord.fileUri ?? undefined,
    durationSeconds: apiRecord.durationSeconds ?? undefined,
    speechSeconds: apiRecord.speechSeconds ?? undefined,
    lineNumber: apiRecord.lineNumber ?? undefined,
    lineCarrier: apiRecord.lineCarrier ?? undefined,
    participantName: apiRecord.participantName ?? undefined,
    updatedAt: apiRecord.updatedAt ?? undefined,
    transcriptPreview: apiRecord.transcriptPreview ?? undefined,
    errorMessage: apiRecord.errorMessage ?? undefined,
    agentId: apiRecord.agentId ?? undefined,
    agentName: apiRecord.agentName ?? undefined,
    projectName: apiRecord.projectName ?? undefined,
    stage: apiRecord.stage ?? undefined,
    stageStatus: apiRecord.stageStatus ?? undefined,
    analysisId: apiRecord.analysisId ?? undefined,
    eventReceivedAt: apiRecord.eventReceivedAt ?? undefined,
    stateUpdatedAt: apiRecord.stateUpdatedAt ?? undefined,
    mediaDetectedAt: apiRecord.mediaDetectedAt ?? undefined,
    textContent: apiRecord.textContent ?? undefined,
    contentHash: apiRecord.contentHash ?? undefined,
    versionTs: apiRecord.versionTs ?? undefined,
    // CRM Entity fields
    crmActivityName: apiRecord.crmActivityName ?? undefined,
    crmEntityType: apiRecord.crmEntityType ?? undefined,
    crmEntityId: apiRecord.crmEntityId ?? undefined,
    crmEntityName: apiRecord.crmEntityName ?? undefined,
    crmVendor: apiRecord.crmVendor ?? undefined,
    crmDomain: apiRecord.crmDomain ?? undefined,
    // Stage snapshot fields (Bitrix24 deal/lead stage at call time, Phase B)
    crmStageId: apiRecord.crmStageId ?? undefined,
    crmPipelineId: apiRecord.crmPipelineId ?? undefined,
    crmStageSemantic: apiRecord.crmStageSemantic ?? undefined,
    crmStageSource: apiRecord.crmStageSource ?? undefined,
    // Level 2 фильтр: звонок короче minCallDurationSeconds ЛИБО чат ниже
    // порога значимости (реплик меньше minChatMessages / оператор не ответил).
    isShortCall: apiRecord.isShortCall ?? undefined,
    minCallDurationSeconds: apiRecord.minCallDurationSeconds ?? undefined,
    // Call category (automatic categorization)
    callCategory: apiRecord.callCategory ?? undefined,
    // Deal outcome badge (Phase C, C-4)
    dealOutcome: apiRecord.dealOutcome ?? null,
    // Stage progression chip (Phase C, C-3/C-4) — crmProgressionEvaluatedAt is
    // the render gate (null = not yet evaluated).
    crmStageProgression: apiRecord.crmStageProgression ?? null,
    crmProgressionEvaluatedAt: apiRecord.crmProgressionEvaluatedAt ?? null,
    // Parse JSON fields from strings to objects
    mediaMeta: safeJsonParse<MediaMeta>(apiRecord.mediaMeta),
    expectedMediaSources: safeJsonParse<MediaSource[]>(apiRecord.expectedMediaSources),
  };
}
